import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardActionArea,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { ServiceInstance } from "../../types";
import { ServiceIcon } from "../common";
import StatusBadge from "./StatusBadge";
import CountdownTimer from "./CountdownTimer";

interface InstanceCardProps {
  instance: ServiceInstance;
  onTerminate?: (instanceId: string) => void;
  onRemove?: (instanceId: string) => void;
  isTerminating?: boolean;
}

const InstanceCard: React.FC<InstanceCardProps> = ({
  instance,
  onTerminate,
  onRemove,
  isTerminating = false,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/instances/${instance.instanceId}`);
  };

  const handleTerminate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTerminate) {
      onTerminate(instance.instanceId);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(instance.instanceId);
    }
  };

  const isActive = ["RUNNING", "PROVISIONING", "PENDING", "STOPPED"].includes(
    instance.status,
  );
  const canTerminate = isActive && !isTerminating;
  const canRemove = !isActive;

  return (
    <Card
      sx={{
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <CardActionArea onClick={handleClick}>
        <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Service Icon */}
          <ServiceIcon
            serviceName={
              instance.serviceName || instance.service?.name || "unknown"
            }
            size={48}
          />

          {/* Instance Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                fontWeight: 600,
              }}
            >
              {instance.serviceName ||
                instance.service?.name ||
                "Unknown Service"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                fontFamily: "monospace",
                wordBreak: "break-all",
                fontSize: { xs: "0.6rem", sm: "0.75rem" },
              }}
            >
              {instance.instanceId}
            </Typography>
          </Box>

          {/* Status & Timer */}
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <StatusBadge status={instance.status} />
            {isActive && instance.expiresAt && (
              <Box sx={{ mt: 0.5 }}>
                <CountdownTimer expiresAt={instance.expiresAt} />
              </Box>
            )}
          </Box>

          {/* Actions */}
          <Box
            sx={{ display: "flex", gap: 0.5, ml: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {instance.domainUrl && (
              <Tooltip title="Open in browser">
                <IconButton
                  size="small"
                  href={instance.domainUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canTerminate && (
              <Tooltip title="Terminate instance">
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleTerminate}
                  disabled={isTerminating}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canRemove && (
              <Tooltip title="Remove from list">
                <IconButton
                  size="small"
                  onClick={handleRemove}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { color: "error.main" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default InstanceCard;
